import { forwardRef } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import Field from "../Field/Field";
import "../Control/Control.css";

export interface TextFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  id: string;
  label?: ReactNode;
  hint?: ReactNode;
  /** text shows under the field; `true` only turns the border red */
  error?: ReactNode | boolean;
  /** icon at the start of the field */
  icon?: ReactNode;
  /** element at the end, for example a show/hide button or a unit */
  trailing?: ReactNode;
  /** md = 52px (default), sm = 44px */
  size?: "sm" | "md";
  className?: string;
}

/**
 * Text input with the label above.
 * Example: <TextField id="email" label="Email" value={v} onChange={fn} />
 */
const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      id,
      label,
      hint,
      error,
      icon,
      trailing,
      size = "md",
      className = "",
      type = "text",
      ...inputProps
    },
    ref
  ) => (
    <Field
      htmlFor={id}
      label={label}
      hint={hint}
      error={error as ReactNode}
      className={className}
    >
      <div
        className={`ds-control ds-control--${size} ${
          inputProps.disabled ? "is-disabled" : ""
        }`}
      >
        {icon && <span className="ds-control__icon">{icon}</span>}
        <input
          ref={ref}
          id={id}
          type={type}
          className="ds-control__input"
          aria-invalid={error ? true : undefined}
          {...inputProps}
        />
        {trailing}
      </div>
    </Field>
  )
);

TextField.displayName = "TextField";

export default TextField;
